import React, { useRef } from 'react';

import { cn } from '@/lib/utils';

import { Input } from '../input';

interface Props {
  value: string[];
  onChange: (val: string[]) => void;
  lengths?: number[];
  labels?: string[];
  error?: boolean;
  classNames?: string[];
  labelClassNames?: string[];
  digiClassNames?: string[];
}

/**
 * A input component for pin code and bank account scenario
 *
 * @component
 * @example
 * const value = ['','','']
 * const type = 'input'
 * const lengths = [3,2,3]
 * const labels = ['l1','l2','l3']
 * const onChange = (value)=>{}
 * return (
 *   <DigiInput value={value} type={type} lengths={lengths} onChange={onChange} />
 * )
 */
export const DigiInput = ({
  value = ['', '', '', ''],
  onChange,
  type = 'password',
  lengths = [1, 1, 1, 1],
  digiClassNames,
  labelClassNames,
  classNames,
  labels = [],
  error = false,
  ...rest
}: Props & Omit<React.HTMLProps<HTMLInputElement>, 'onChange' | 'value'>) => {
  let digits = [...value];
  let ref = useRef<HTMLDivElement>(null);

  let handleInput = (i: number, val: string) => {
    digits[i] = val;
    if (val && val.length === lengths[i]) {
      let parent = ref.current;
      let last = i === digits.length - 1;
      if (parent && !last) {
        let input = parent.querySelectorAll('input')[i + 1] as HTMLInputElement;
        input?.focus();
      }
    }
    onChange(digits);
  };
  return (
    <div ref={ref} className="flex w-full flex-wrap gap-2">
      {digits.map((digi, i) => {
        return (
          <div key={i} className={cn(classNames?.[i])}>
            {labels[i] && (
              <label
                className={cn('sky-mark-regular-16 text-primary-midnight', labelClassNames?.[i])}
              >
                {labels[i]}
              </label>
            )}
            <Input
              type={type}
              className={cn({ 'border-secondary-red': error }, digiClassNames?.[i])}
              value={digi}
              key={i}
              maxLength={lengths[i]}
              onChange={e => handleInput(i, e.target.value)}
              {...rest}
            />
          </div>
        );
      })}
    </div>
  );
};
